import { useEffect, useRef, useCallback, useState } from 'react';
import html2canvas from 'html2canvas';
import { useInterview } from '../../contexts/InterviewContext';

interface ScreenshotHandlerProps {
  isActive: boolean; // Only active for implementor interviews
  implementorUniqueId?: string;
}

interface Screenshot {
  number: number;
  timestamp: string;
  image_data: string; // base64 encoded
}

/**
 * ScreenshotHandler Component
 * 
 * Captures full-page screenshots for implementor interviews at:
 * - 1 minute
 * - 3 minutes  
 * - 5 minutes
 * 
 * Sends screenshots to backend API for storage and webhook delivery
 */
const ScreenshotHandler: React.FC<ScreenshotHandlerProps> = ({
  isActive,
  implementorUniqueId,
}) => {
  const { elapsedTime, isExitingInterview } = useInterview();
  const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
  const capturedTimestampsRef = useRef<Set<number>>(new Set());
  const screenshotsRef = useRef<Screenshot[]>([]);
  const sendingRef = useRef(false);
  const finalScreenshotCapturedRef = useRef(false);
  
  // Define screenshot capture times (in seconds)
  const SCREENSHOT_TIMES = [60, 180, 300]; // 1 min, 3 min, 5 min

  /**
   * Send screenshots to backend API
   */
  const sendScreenshotsToBackend = useCallback(async (screenshotsData: Screenshot[]) => {
    if (!implementorUniqueId || screenshotsData.length === 0 || sendingRef.current) {
      return;
    }

    sendingRef.current = true;

    try {
      console.log(`[ScreenshotHandler] Sending ${screenshotsData.length} screenshots to backend...`);
      
      // Use the Python backend URL (same as interview proxy)
      const backendUrl = process.env.REACT_APP_INTERVIEW_PROXY_API_URL || 'http://localhost:8000';
      
      const response = await fetch(`${backendUrl}/api/implementor-screenshots`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          unique_id: implementorUniqueId,
          screenshots: screenshotsData,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to send screenshots: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[ScreenshotHandler] Screenshots sent successfully:', result);
    } catch (error) {
      console.error('[ScreenshotHandler] Failed to send screenshots to backend:', error);
    } finally {
      sendingRef.current = false;
    }
  }, [implementorUniqueId]);

  /**
   * Capture full page screenshot using html2canvas
   */
  const captureScreenshot = useCallback(async (timeInSeconds: number): Promise<Screenshot | null> => {
    try {
      console.log(`[ScreenshotHandler] Capturing screenshot at ${timeInSeconds}s`);
      
      // Find the main interview container with fallback chain for reliability
      const interviewContainer = document.getElementById('interview-capture-container') || 
                                 document.querySelector('[data-interview-page="true"]') ||
                                 document.querySelector('.min-h-screen') as HTMLElement;
      
      if (!interviewContainer) {
        console.error('[ScreenshotHandler] Interview container not found');
        return null;
      }

      // Capture the screenshot using html2canvas
      const canvas = await html2canvas(interviewContainer, {
        allowTaint: true,
        useCORS: true,
        logging: false,
        scale: 1, // Full scale for 720p+ quality
        width: Math.max(interviewContainer.offsetWidth, 1280), // Minimum 720p width
        height: Math.max(interviewContainer.offsetHeight, 720), // Minimum 720p height
        backgroundColor: null, // Preserve actual page background (supports dark mode)
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
      });

      // Convert to base64 with good quality
      const imageData = canvas.toDataURL('image/jpeg', 0.85); // JPEG with 85% quality for better clarity
      
      const screenshot: Screenshot = {
        number: screenshotsRef.current.length + 1,
        timestamp: new Date().toISOString(),
        image_data: imageData,
      };

      console.log(`[ScreenshotHandler] Screenshot ${screenshot.number} captured successfully`);
      return screenshot;
    } catch (error) {
      console.error('[ScreenshotHandler] Failed to capture screenshot:', error);
      return null;
    }
  }, []);

  /**
   * Check if it's time to capture a screenshot
   */
  useEffect(() => {
    if (!isActive || !implementorUniqueId) {
      return;
    }

    // Check each screenshot time
    SCREENSHOT_TIMES.forEach(async (captureTime) => {
      // Check if we're at or past this capture time and haven't captured it yet
      if (elapsedTime >= captureTime && !capturedTimestampsRef.current.has(captureTime)) {
        // Mark as captured to prevent duplicates
        capturedTimestampsRef.current.add(captureTime);
        
        console.log(`[ScreenshotHandler] Time reached: ${captureTime}s, capturing screenshot...`);
        
        // Capture screenshot
        const screenshot = await captureScreenshot(captureTime);
        
        if (screenshot) {
          // Update both state and ref
          setScreenshots(prev => {
            const updated = [...prev, screenshot];
            screenshotsRef.current = updated;
            
            // Send screenshots to backend after each capture
            sendScreenshotsToBackend(updated);
            
            return updated;
          });
          
          console.log(`[ScreenshotHandler] Total screenshots captured: ${screenshotsRef.current.length}`);
        }
      }
    });
  }, [isActive, elapsedTime, implementorUniqueId, captureScreenshot, sendScreenshotsToBackend]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      console.log('[ScreenshotHandler] Component unmounting, clearing captured timestamps');
      capturedTimestampsRef.current.clear();
    };
  }, []);

  /**
   * Capture final screenshot when interview ends early (before 5 minutes)
   */
  useEffect(() => {
    if (!isActive || !implementorUniqueId || !isExitingInterview || finalScreenshotCapturedRef.current) {
      return;
    }

    // Check if we have less than 3 screenshots (interview ended before 5 minutes)
    if (screenshotsRef.current.length < 3) {
      console.log('[ScreenshotHandler] Interview ending early, will capture final screenshot after 1.5s delay...');
      finalScreenshotCapturedRef.current = true;

      const timer = setTimeout(async () => {
        console.log('[ScreenshotHandler] Capturing final screenshot on early exit...');
        
        const screenshot = await captureScreenshot(-1); // -1 indicates final screenshot
        
        if (screenshot) {
          setScreenshots(prev => {
            const updated = [...prev, screenshot];
            screenshotsRef.current = updated;
            
            // Send final screenshots to backend
            sendScreenshotsToBackend(updated);
            
            console.log(`[ScreenshotHandler] Final screenshot captured. Total: ${updated.length}`);
            return updated;
          });
        }
      }, 1500); // 1.5 second delay

      return () => clearTimeout(timer);
    }
  }, [isActive, implementorUniqueId, isExitingInterview, captureScreenshot, sendScreenshotsToBackend]);

  // This component doesn't render anything
  return null;
};

export default ScreenshotHandler;
